import { inject, Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  GetMyNotificationsResponse,
  GetNotificationCountsResponse,
  GetUnreadNotificationCountResponse,
  NotificationsService,
} from '@smatchy/sdk';
import { catchError, concatMap, forkJoin, map, Observable, of, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  DeleteNotificationAction,
  LoadMoreNotificationsAction,
  LoadNotificationInboxAction,
  LoadUnreadNotificationCountAction,
  MarkAllNotificationsReadAction,
  MarkNotificationReadAction,
  MarkNotificationUnreadAction,
  RefreshNotificationMetricsAction,
} from './notification-inbox.actions';
import {
  NotificationFilterId,
  NotificationInboxCountsDto,
  NotificationInboxItemDto,
  toApiFilter,
} from '../domains/notification-inbox';

export interface NotificationInboxStateModel {
  items: NotificationInboxItemDto[];
  loading: boolean;
  loadingMore: boolean;
  haveNext: boolean;
  nextCursor?: string;
  unreadCount: number;
  counts: NotificationInboxCountsDto | null;
  activeFilter: NotificationFilterId;
  search: string;
  loadError: boolean;
  loadMoreError: boolean;
}

const defaults: NotificationInboxStateModel = {
  items: [],
  loading: false,
  loadingMore: false,
  haveNext: false,
  unreadCount: 0,
  counts: null,
  activeFilter: 'all',
  search: '',
  loadError: false,
  loadMoreError: false,
};

type SdkWrappedResponse<T> = {
  error?: unknown;
  value?: T;
};

@State<NotificationInboxStateModel>({
  name: 'notificationInbox',
  defaults,
})
@Injectable()
export class NotificationInboxState {
  private readonly pageSize = 50;
  private readonly maxPrefetchedItems = 200;
  private readonly notificationsService = inject(NotificationsService);

  @Action(LoadNotificationInboxAction)
  loadInbox(
    { patchState, getState }: StateContext<NotificationInboxStateModel>,
    { filter, search, refresh }: LoadNotificationInboxAction,
  ) {
    patchState({
      loading: true,
      activeFilter: filter,
      search: search ?? '',
      loadError: false,
      loadMoreError: false,
    });

    return this.fetchInboxPage(filter, search).pipe(
      tap((inbox) => {
        patchState({
          loading: false,
          loadError: false,
          items: inbox.notifications,
          haveNext: inbox.haveNext,
          nextCursor: inbox.nextCursor,
        });
      }),
      concatMap(() => this.prefetchRemainingPages(getState, patchState)),
      catchError(() => {
        patchState({
          loading: false,
          loadError: true,
          items: refresh ? [] : getState().items,
        });
        return of(null);
      }),
    );
  }

  @Action(LoadMoreNotificationsAction)
  loadMore({ patchState, getState }: StateContext<NotificationInboxStateModel>) {
    const state = getState();
    if (!state.haveNext || !state.nextCursor || state.loadingMore) {
      return of(null);
    }

    patchState({ loadingMore: true, loadMoreError: false });

    return this.fetchInboxPage(state.activeFilter, state.search, state.nextCursor).pipe(
      tap((inbox) => {
        patchState({
          loadingMore: false,
          loadMoreError: false,
          items: [...getState().items, ...inbox.notifications],
          haveNext: inbox.haveNext,
          nextCursor: inbox.nextCursor,
        });
      }),
      catchError(() => {
        patchState({ loadingMore: false, loadMoreError: true });
        return of(null);
      }),
    );
  }

  @Action(LoadUnreadNotificationCountAction)
  loadUnreadCount({ patchState }: StateContext<NotificationInboxStateModel>) {
    return this.notificationsService.getUnreadCount().pipe(
      map((res) => this.unwrapValue<GetUnreadNotificationCountResponse>(res)),
      tap((value) => patchState({ unreadCount: value.count ?? 0 })),
      catchError(() => of(null)),
    );
  }

  @Action(RefreshNotificationMetricsAction)
  refreshMetrics({ patchState }: StateContext<NotificationInboxStateModel>) {
    return forkJoin({
      unread: this.notificationsService.getUnreadCount(),
      counts: this.notificationsService.getCounts(),
    }).pipe(
      tap(({ unread, counts }) => {
        const unreadValue = this.unwrapValue<GetUnreadNotificationCountResponse>(unread);
        const countsValue = this.unwrapValue<GetNotificationCountsResponse>(counts);
        patchState({
          unreadCount: unreadValue.count ?? 0,
          counts: (countsValue.counts as NotificationInboxCountsDto) ?? null,
        });
      }),
      catchError(() => of(null)),
    );
  }

  @Action(MarkNotificationReadAction)
  markRead(
    { patchState, getState }: StateContext<NotificationInboxStateModel>,
    { notificationId }: MarkNotificationReadAction,
  ) {
    return this.notificationsService.markRead({ id: notificationId }).pipe(
      map((res) => this.unwrapValue(res)),
      tap(() => {
        const items = getState().items.map((item) =>
          item.id === notificationId ? { ...item, isUnread: false } : item,
        );
        patchState({
          items,
          unreadCount: Math.max(0, getState().unreadCount - 1),
        });
      }),
      catchError(() => of(null)),
    );
  }

  @Action(MarkNotificationUnreadAction)
  markUnread(
    { patchState, getState }: StateContext<NotificationInboxStateModel>,
    { notificationId }: MarkNotificationUnreadAction,
  ) {
    const wasRead = getState().items.find((item) => item.id === notificationId)?.isUnread === false;
    return this.notificationsService.markUnread({ id: notificationId }).pipe(
      map((res) => this.unwrapValue(res)),
      tap(() => {
        const items = getState().items.map((item) => (item.id === notificationId ? { ...item, isUnread: true } : item));
        patchState({
          items,
          unreadCount: wasRead ? getState().unreadCount + 1 : getState().unreadCount,
        });
      }),
      catchError(() => of(null)),
    );
  }

  @Action(MarkAllNotificationsReadAction)
  markAllRead({ patchState, getState }: StateContext<NotificationInboxStateModel>) {
    return this.notificationsService.markAllRead().pipe(
      map((res) => this.unwrapValue(res)),
      tap(() => {
        const counts = getState().counts;
        patchState({
          items: getState().items.map((item) => ({ ...item, isUnread: false })),
          unreadCount: 0,
          counts: counts
            ? {
                ...counts,
                unread: 0,
                unreadByCategory: {
                  events: 0,
                  finance: 0,
                  groups: 0,
                  accounts: 0,
                },
              }
            : null,
        });
      }),
      catchError((error) => throwError(() => error)),
    );
  }

  @Action(DeleteNotificationAction)
  deleteNotification(
    { patchState, getState }: StateContext<NotificationInboxStateModel>,
    { notificationId }: DeleteNotificationAction,
  ) {
    const removed = getState().items.find((item) => item.id === notificationId);
    return this.notificationsService.deleteNotification({ id: notificationId }).pipe(
      map((res) => this.unwrapValue(res)),
      tap(() => {
        patchState({
          items: getState().items.filter((item) => item.id !== notificationId),
          unreadCount: removed?.isUnread ? Math.max(0, getState().unreadCount - 1) : getState().unreadCount,
        });
      }),
      catchError((error) => throwError(() => error)),
    );
  }

  private fetchInboxPage(filter: NotificationFilterId, search?: string, cursor?: string) {
    return this.notificationsService
      .getMyNotifications({
        filter: toApiFilter(filter) as unknown as object,
        search: search?.trim() || undefined,
        cursor,
        count: this.pageSize,
      })
      .pipe(
        map((res) => this.unwrapValue<GetMyNotificationsResponse>(res)),
        map((inbox) => ({
          notifications: (inbox.notifications ?? []) as unknown as NotificationInboxItemDto[],
          haveNext: inbox.haveNext ?? false,
          nextCursor: inbox.nextCursor,
        })),
        catchError((error) => throwError(() => error)),
      );
  }

  /**
   * The return type is written out because this method calls itself: without it
   * the compiler has nothing to infer from and gives up with an implicit any.
   */
  private prefetchRemainingPages(
    getState: () => NotificationInboxStateModel,
    patchState: StateContext<NotificationInboxStateModel>['patchState'],
  ): Observable<unknown> {
    const state = getState();
    if (!state.haveNext || !state.nextCursor || state.items.length >= this.maxPrefetchedItems) {
      return of(null);
    }

    return this.fetchInboxPage(state.activeFilter, state.search, state.nextCursor).pipe(
      tap((inbox) => {
        patchState({
          items: [...getState().items, ...inbox.notifications],
          haveNext: inbox.haveNext,
          nextCursor: inbox.nextCursor,
        });
      }),
      concatMap(() => this.prefetchRemainingPages(getState, patchState)),
      catchError(() => of(null)),
    );
  }

  private unwrapValue<T>(response: SdkWrappedResponse<T>): T {
    if (response.error) {
      throw response.error;
    }
    if (!response.value) {
      throw new Error('Notification API response missing value');
    }
    return response.value;
  }
}
